/*!
 * Copyright © 2018-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { EDriverWorkingState } from 'tacho';

import { isElementNull } from '../../../../utils';
import { IFlatTreeNode, IHierarchyTreeData } from '../../../core/components';
import {
  EDriverStatusSortType,
  EVehicleDriverPanelTabs,
  EVehicleSelectionMode,
  EVehiclesSortType,
  EVehicleStatusType,
  IDriverStatus,
  IMapContextMenuData,
  IScrollItem,
  ISignalStatusUpdate,
  IZoomToMarkerParams,
  MarkerInfo
} from '../../../core/models';
import { ECheckboxState } from '../../../core/models/checkbox-state.enum';
import { MapAnimations } from '../../../maps/animations';
import { StatusSortOptionsComponent } from '../../../shared';
import { IVehicleTreeNodeRightClickData } from '../../models';
import { VehicleSelectionPanelComponent } from '../vehicle-selection-panel/vehicle-selection-panel.component';

@Component({
  selector: 'fm-vehicle-driver-panel-large',
  templateUrl: './vehicle-driver-panel-large.component.html',
  styleUrls: ['./vehicle-driver-panel-large.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [MapAnimations.panelState]
})
export class VehicleDriverPanelLargeComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  public activeTab: EVehicleDriverPanelTabs;
  @Input()
  public collapseAndSetupRestore$: Observable<void>;

  @Input()
  public set isCollapsed(isCollapsed: boolean) {
    this.isPanelCollapsed = isCollapsed;
  }

  @Input()
  panelHeightPx: number;
  @Input()
  public maxPlottableVehicles: number;
  @Input()
  public restoreIsCollapsed$: Observable<void>;
  @Input()
  public vehicleListScopingTreeData: IFlatTreeNode<string>[];
  @Input()
  public vehicleListTreeData: IHierarchyTreeData<string>;
  @Input()
  public vehicleSelectionMode: EVehicleSelectionMode;
  @Input()
  public vehicleSortTypeSelected: EVehiclesSortType;
  @Input()
  public isVehicleScopingEnabled: boolean;
  @Input()
  public isAssetsEnabled: boolean;
  @Input()
  public showTachoInformation: boolean;
  @Input()
  public driverStatusData: IDriverStatus[];
  @Input()
  public driverStatusFilter: EDriverWorkingState;
  @Input()
  public isDriverBreakDueFilterSelected: boolean;
  @Input()
  public driverStatusSortType: EDriverStatusSortType;
  @Input()
  public isDriverPlannerPageAllowed: boolean;
  @Input()
  public anyPoweredAssets: boolean;
  @Input()
  public anyNonPoweredAssets: boolean;
  @Input()
  public statusFilterSelected: EVehicleStatusType;
  @Input()
  public statusFilterCounts: Map<EVehicleStatusType, number>;
  @Input()
  isStatusPlotsLoaded: boolean;
  @Input()
  itemToScrollTo: IScrollItem;
  @Input()
  isChargeableEVInFleet: boolean;
  @Input()
  isPingMVPEnabled: boolean;


  @Output()
  public activeTabChange = new EventEmitter<EVehicleDriverPanelTabs>();
  @Output()
  public isCollapsedChange = new EventEmitter<boolean>();
  @Output()
  public vehicleListEditScopingClick = new EventEmitter<TemplateRef<any>>();
  @Output()
  public vehicleListScopingClose = new EventEmitter<void>();
  @Output()
  public vehicleListScopingSave = new EventEmitter<string[]>();
  @Output()
  public vehicleListSelectFocusVehicle = new EventEmitter<string>();
  @Output()
  public vehicleListSelectionChange = new EventEmitter<Map<string, ECheckboxState>>();
  @Output()
  public vehicleListSelectionModeChange = new EventEmitter<EVehicleSelectionMode>();
  @Output()
  public sortParamChange: EventEmitter<EVehiclesSortType> = new EventEmitter();
  @Output()
  public vehicleListSelectionChanged: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output()
  public zoomTo = new EventEmitter<IZoomToMarkerParams>();
  @Output()
  public signalChanges: EventEmitter<ISignalStatusUpdate[]> = new EventEmitter();
  @Output()
  public openVehicleContextMenu = new EventEmitter<IMapContextMenuData<MarkerInfo>>();
  @Output()
  public openVehicleListSelectionContextMenu = new EventEmitter<IVehicleTreeNodeRightClickData>();
  @Output()
  public highlightMarker = new EventEmitter<number>();
  @Output()
  public removeMarkerHighlight = new EventEmitter<void>();
  @Output()
  public filterChange: EventEmitter<EDriverWorkingState> = new EventEmitter();
  @Output()
  public driverBreakDueFilterChanged: EventEmitter<boolean> = new EventEmitter();
  @Output()
  public driverStatusSortTypeChanged: EventEmitter<EDriverStatusSortType> = new EventEmitter();
  @Output()
  public openDriverPlanner = new EventEmitter<void>();
  @Output()
  public statusFilterChanged = new EventEmitter<EVehicleStatusType>();

  @HostBinding('class.floating-panel')
  public floatingPanelClass = true;
  @HostBinding('@panelState')
  public get panelState(): {
    value: string;
    params: {
      panelHeight: string;
    };
  } {
    const state = this.isPanelCollapsed ? 'collapsed' : 'expanded';
    const height = isElementNull(this.panelHeightPx) ? '*' : `${this.panelHeightPx}px`;
    return {
      value: state,
      params: {
        panelHeight: height
      }
    };
  }

  @ViewChild(VehicleSelectionPanelComponent)
  vehicleSelectionPanel: VehicleSelectionPanelComponent;
  @ViewChild(StatusSortOptionsComponent)
  statusSortOptions: StatusSortOptionsComponent;

  public EVehicleDriverPanelTabs = EVehicleDriverPanelTabs;
  public EVehiclesSortType = EVehiclesSortType;
  public isPanelCollapsed: boolean;
  public hasFleetSelected: boolean;
  public hasFleetConnected: boolean;
  public isStatusPanelReady = false;

  private _lastIsCollapsed: boolean;
  private _shouldRestoreIsCollapsed: boolean;
  private readonly _unsubscribe$ = new Subject<void>();

  constructor(private readonly _changeDetectorRef: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.statusFilterCounts?.currentValue) {
      this._hasFleetSelected();
    }

    if (changes?.vehicleListTreeData?.currentValue) {
      this._hasFleetConnected();
    }
    this.isStatusPanelReady = true;
  }

  public ngOnInit(): void {
    this.collapseAndSetupRestore$
      .pipe(
        takeUntil(this._unsubscribe$),
        filter(() => !this.isPanelCollapsed)
      )
      .subscribe(() => {
        this._lastIsCollapsed = this.isPanelCollapsed;
        this._shouldRestoreIsCollapsed = true;
        this.isPanelCollapsed = true;
        this._changeDetectorRef.markForCheck();
      });

    this.restoreIsCollapsed$
      .pipe(
        filter(() => this._shouldRestoreIsCollapsed),
        takeUntil(this._unsubscribe$)
      )
      .subscribe(() => {
        this.isPanelCollapsed = this._lastIsCollapsed;
        this._changeDetectorRef.markForCheck();
      });
  }

  public ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  @HostListener('@panelState.done')
  public onPanelStateTransitionDone(): void {
    if (!this.isPanelCollapsed && this.activeTab === EVehicleDriverPanelTabs.VehicleList) {
      this.vehicleSelectionPanel.onViewportChange();
    }
  }

  public onTabChange(): void {
    this._updateIsCollapsed(false);
  }

  public onVehicleListEditScopingClick(modalTemplate: TemplateRef<any>): void {
    this.vehicleListEditScopingClick.emit(modalTemplate);
  }

  public onVehicleListScopingClose(): void {
    this.vehicleListScopingClose.emit();
  }

  public onVehicleListScopingSave(selectedGroups: string[]): void {
    this.vehicleListScopingSave.emit(selectedGroups);
  }

  public onVehicleListSelectFocusVehicle(vehicleId: string) {
    this.vehicleListSelectFocusVehicle.emit(vehicleId);
  }

  public onVehicleListSelectionChange(vehicleSelection: Map<string, ECheckboxState>) {
    this.vehicleListSelectionChange.emit(vehicleSelection);
  }

  public onVehicleListSelectionModeChange(mode: EVehicleSelectionMode) {
    this.vehicleListSelectionModeChange.emit(mode);
  }

  public selectTab(tab: EVehicleDriverPanelTabs): void {
    this.activeTabChange.emit(tab);
  }

  public toggleIsCollapsed(): void {
    this._updateIsCollapsed(!this.isPanelCollapsed);
  }

  public onSortParamChange(type: EVehiclesSortType): void {
    this.sortParamChange.emit(type);
  }

  public zoomToPosition(params: IZoomToMarkerParams): void {
    this.zoomTo.emit(params);
  }

  public onOpenDriverStatusContextMenu(menuData: IMapContextMenuData<MarkerInfo>): void {
    this.openVehicleContextMenu.emit(menuData);
  }

  public onVehicleListSelectionRightClick(vehicleRightClickData: IVehicleTreeNodeRightClickData): void {
    this.openVehicleListSelectionContextMenu.emit(vehicleRightClickData);
  }

  public onHighlightMarker(vehicleId: number): void {
    this.highlightMarker.emit(vehicleId);
  }

  public onRemoveMarkerHighlight(): void {
    this.removeMarkerHighlight.emit();
  }

  public onFilterChange(driverState: EDriverWorkingState): void {
    this.filterChange.emit(driverState);
  }

  public onDriverBreakDueFilterSelectedChange(selectedBreakDue: boolean): void {
    this.driverBreakDueFilterChanged.emit(selectedBreakDue);
  }

  public onStatusSortTypeChange(statusSortType: EDriverStatusSortType): void {
    this.driverStatusSortTypeChanged.emit(statusSortType);
  }

  public onOpenDriverPlanner(): void {
    this.openDriverPlanner.emit();
  }

  public onStatusFilterChange(filterSelected: EVehicleStatusType): void {
    this.statusFilterChanged.emit(filterSelected);
  }

  public openVehicleDriverSortPanel(): void {
    this.statusSortOptions.openDropdownPanel();
  }

  private _hasFleetConnected(): void {
    this.hasFleetConnected = this.vehicleListTreeData.data && this.vehicleListTreeData.data.length > 1;
  }

  private _hasFleetSelected(): void {
    this.hasFleetSelected = this.statusFilterCounts?.get(EVehicleStatusType.All) > 0;
  }

  private _updateIsCollapsed(isCollapsed: boolean): void {
    this.isPanelCollapsed = isCollapsed;
    this._shouldRestoreIsCollapsed = false;
    this.isCollapsedChange.emit(this.isPanelCollapsed);
  }
}
